import * as Select from '@radix-ui/react-select';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DropdownItemClear } from '~/chords/DropdownSingleSelect/components/DropdownItemClear';
import { DropdownItems } from '~/chords/DropdownSingleSelect/components/DropdownItems';
import { DropdownNoOptionsAvailable } from '~/chords/DropdownSingleSelect/components/DropdownNoOptionsAvailable';
import { DropdownSearch } from '~/chords/DropdownSingleSelect/components/DropdownSearch';
import type {
  DropdownClearOptionsInternal,
  DropdownContentOptions,
  DropdownSearchOptions,
  HeadingTemplateComponent,
  InternalDropdownOption,
  InternalDropdownOptions,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownSingleSelect/types';
import { cn } from '~/chords/lib/cn';
import { Scrollbars } from '~/chords/Scrollbars';

interface DropdownMenuProps extends RadixUiProps {
  options: InternalDropdownOptions;
  contentOptions: DropdownContentOptions;
  searchOptions?: DropdownSearchOptions;
  clearOptions: DropdownClearOptionsInternal;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedOption?: InternalDropdownOption;
  onSelectOption: (option: InternalDropdownOption) => void;
  showCheckIcon?: boolean;
  HeadingTemplate?: HeadingTemplateComponent;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownMenu({
  options,
  contentOptions,
  searchOptions,
  clearOptions,
  searchQuery,
  onSearchQueryChange,
  selectedOption,
  onSelectOption,
  showCheckIcon,
  HeadingTemplate,
  OptionTemplate,
  radix = {},
}: DropdownMenuProps) {
  const { t } = useTranslation('common');
  const isOneOrMoreOptionsVisible = options.some((group) => {
    if ('options' in group) {
      return group.options.some((option) => option.isVisible);
    }

    return group.some((option) => option.isVisible);
  });

  const hasAnyOptions = options.some((group) => {
    if ('options' in group) {
      return group.options.length > 0;
    }

    return group.length > 0;
  });

  const searchQueryTrimmed = searchQuery.trim();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<number | undefined>(undefined);
  const [refocusTrigger, setRefocusTrigger] = useState(0);
  const isExternalQueryEmpty =
    searchOptions?.disableInternalFiltering && !searchQueryTrimmed;

  useEffect(() => {
    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, []);

  // Track when options change and increment trigger
  useEffect(() => {
    if (searchOptions?.focusOnOpen) {
      setRefocusTrigger((prev) => prev + 1);
    }
  }, [options, searchOptions?.focusOnOpen]);

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    // If the search input field is empty or not enabled, allow the dropdown to close as normal.
    if (!searchOptions?.isEnabled || !searchQueryTrimmed) {
      return;
    }

    // If the search input field is populated and the user presses the Escape key,
    // clear the search input and focus the cursor into it.
    event.preventDefault();

    onSearchQueryChange('');

    timeoutRef.current = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 10);
  };

  const showClearOptionTop =
    clearOptions?.isEnabled &&
    (clearOptions.placements.includes('option-top') ||
      clearOptions.placements.includes('default')) &&
    selectedOption &&
    !searchQueryTrimmed;

  const radixWithKeyboardNav: typeof radix = {
    ...radix,
    selectGroup: {
      ...radix.selectGroup,
      onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
        const targetElement = event.target as HTMLElement;
        const isFirstOption = showClearOptionTop
          ? targetElement?.dataset?.clearSelectItem !== undefined
          : event.target === event.currentTarget.firstChild;

        // Navigate to the search input when the user presses ArrowUp on the first option.
        if (event.key === 'ArrowUp' && isFirstOption) {
          searchInputRef.current?.focus();
        }
      },
    },
  };

  const ClearOption = clearOptions.OptionTemplate ?? DropdownItemClear;

  return (
    <Select.Portal>
      <Select.Content
        // Prevents the dropdown menu from overlapping the dropdown trigger.
        // https://www.radix-ui.com/primitives/docs/components/select#content
        position="popper"
        {...radixWithKeyboardNav.selectContent}
        className={cn(
          'mt-xs max-w-[var(--radix-popper-available-width)] overflow-auto text-body-sm text-default',
          'rounded-xs border shadow-xl drop-shadow-xl bg-level-03 border-emphasis-strong',
          radixWithKeyboardNav.selectContent?.className,
        )}
        style={
          {
            // This is used to calculate the scroll height of the dropdown menu.
            '--dropdown-menu-search-height': searchOptions?.isEnabled
              ? '72px'
              : '0px',
            '--dropdown-menu-clear-option-height': showClearOptionTop
              ? '36px'
              : '0px',
            '--dropdown-menu-max-height': contentOptions.maxHeight?.trim()
              ? contentOptions.maxHeight.trim()
              : '600px',
            zIndex: contentOptions.zIndex,
            minWidth:
              'min(max(var(--radix-popper-anchor-width), 280px), var(--radix-popper-available-width))',
          } as React.CSSProperties
        }
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        {!searchQuery &&
        !hasAnyOptions &&
        !searchOptions?.disableInternalFiltering ? (
          <DropdownNoOptionsAvailable radix={radixWithKeyboardNav} />
        ) : (
          <>
            {searchOptions?.isEnabled ? (
              <DropdownSearch
                value={searchQuery}
                onChange={onSearchQueryChange}
                inputRef={searchInputRef}
                focusOnOpen={searchOptions.focusOnOpen}
                refocusTrigger={refocusTrigger}
                maxLength={searchOptions.maxLength}
              />
            ) : null}

            {showClearOptionTop ? (
              <ClearOption radix={radixWithKeyboardNav} />
            ) : null}

            <Select.Viewport {...radixWithKeyboardNav.selectViewport}>
              <Scrollbars asChild>
                <div className="max-h-dropdown-single-select-scroll">
                  {isOneOrMoreOptionsVisible || isExternalQueryEmpty ? (
                    <DropdownItems
                      options={options}
                      searchQuery={searchQueryTrimmed}
                      selectedOption={selectedOption}
                      onSelectOption={onSelectOption}
                      showCheckIcon={showCheckIcon}
                      HeadingTemplate={HeadingTemplate}
                      OptionTemplate={OptionTemplate}
                      radix={radixWithKeyboardNav}
                    />
                  ) : (
                    <DropdownNoOptionsAvailable
                      labelText={t('dropdownTree.noMatchingOptionsText', {
                        searchQuery: searchQueryTrimmed,
                      })}
                      labelNode={
                        <span className="text-emphasis-stronger">
                          <Trans
                            i18nKey="dropdownTree.noMatchingOptionsHtml"
                            t={t}
                            values={{
                              searchQuery: searchQueryTrimmed,
                            }}
                            components={{
                              strong: (
                                <span className="font-semibold text-default" />
                              ),
                            }}
                          />
                        </span>
                      }
                      radix={radixWithKeyboardNav}
                    />
                  )}
                </div>
              </Scrollbars>
            </Select.Viewport>
          </>
        )}
      </Select.Content>
    </Select.Portal>
  );
}

DropdownMenu.displayName = 'DropdownMenu';
